import { api } from './api';

export interface Commission {
  id: string;
  userId: string;
  saleId: string;
  saleTotal: number;
  percentage: number;
  amount: number;
  isPaid: boolean;
  paidAt?: string;
  createdAt: string;
  user: {
    id: string;
    name: string;
    employeeCode?: string;
  };
  sale: {
    id: string;
    total: number;
    createdAt: string;
    customerName?: string;
  };
}

export interface CommissionsResponse {
  success: boolean;
  commissions: Commission[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
}

export interface EmployeeCommissionsResponse {
  success: boolean;
  commissions: Commission[];
  summary: {
    totalCommissions: number;
    paidCommissions: number;
    pendingCommissions: number;
  };
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
}

export interface CommissionsSummary {
  success: boolean;
  summary: {
    total: {
      amount: number;
      count: number;
    };
    paid: {
      amount: number;
      count: number;
    };
    pending: {
      amount: number;
      count: number;
    };
    topEmployees: Array<{
      userId: string;
      user: {
        id: string;
        name: string;
        employeeCode?: string;
      };
      totalAmount: number;
      commissionCount: number;
    }>;
  };
}

export const commissionsApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getCommissions: builder.query<CommissionsResponse, {
      page?: number;
      limit?: number;
      userId?: string;
      isPaid?: boolean;
      startDate?: string;
      endDate?: string;
    }>({
      query: (params = {}) => {
        const searchParams = new URLSearchParams();
        
        if (params.page) searchParams.append('page', params.page.toString());
        if (params.limit) searchParams.append('limit', params.limit.toString());
        if (params.userId) searchParams.append('userId', params.userId);
        if (params.isPaid !== undefined) searchParams.append('isPaid', params.isPaid.toString());
        if (params.startDate) searchParams.append('startDate', params.startDate);
        if (params.endDate) searchParams.append('endDate', params.endDate);
        // Add timestamp to prevent caching issues
        searchParams.append('_t', Date.now().toString());
        
        return {
          url: `/commissions?${searchParams.toString()}`,
          method: 'GET',
        };
      },
      providesTags: ['Commission'],
    }),
    
    getEmployeeCommissions: builder.query<EmployeeCommissionsResponse, {
      userId: string;
      page?: number;
      limit?: number;
      isPaid?: boolean;
      startDate?: string;
      endDate?: string;
    }>({
      query: ({ userId, ...params }) => {
        const searchParams = new URLSearchParams();
        
        if (params.page) searchParams.append('page', params.page.toString());
        if (params.limit) searchParams.append('limit', params.limit.toString());
        if (params.isPaid !== undefined) searchParams.append('isPaid', params.isPaid.toString());
        if (params.startDate) searchParams.append('startDate', params.startDate);
        if (params.endDate) searchParams.append('endDate', params.endDate);
        
        return {
          url: `/commissions/employee/${userId}?${searchParams.toString()}`,
          method: 'GET',
        };
      },
      providesTags: ['Commission'],
    }),
    
    getCommissionsSummary: builder.query<CommissionsSummary, {
      startDate?: string;
      endDate?: string;
      userId?: string;
    }>({
      query: (params = {}) => {
        const searchParams = new URLSearchParams();
        
        if (params.startDate) searchParams.append('startDate', params.startDate);
        if (params.endDate) searchParams.append('endDate', params.endDate);
        if (params.userId) searchParams.append('userId', params.userId);
        // Add timestamp to prevent caching issues
        searchParams.append('_t', Date.now().toString());
        
        return {
          url: `/commissions/summary?${searchParams.toString()}`,
          method: 'GET',
        };
      },
      providesTags: ['Commission'],
    }),
    
    getPendingCommissionsByEmployee: builder.query<{
      success: boolean;
      data: Array<{
        userId: string;
        user: {
          id: string;
          name: string;
          employeeCode?: string;
          commissionRate: number;
        };
        totalAmount: number;
        commissionCount: number;
      }>;
    }, void>({
      query: () => ({
        url: `/commissions/pending?_t=${Date.now()}`,
        method: 'GET',
      }),
      providesTags: ['Commission'],
    }),
    
    markCommissionsAsPaid: builder.mutation<{ success: boolean }, { commissionIds: string[] }>({
      query: (data) => ({
        url: '/commissions/mark-paid',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Commission'],
    }),
    
    createCommission: builder.mutation<{ success: boolean; data: Commission }, {
      userId: string;
      saleId: string;
      amount?: number;
      percentage?: number;
    }>({
      query: (data) => ({
        url: '/commissions',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Commission'],
    }),
  }),
});

export const {
  useGetCommissionsQuery,
  useGetEmployeeCommissionsQuery,
  useGetCommissionsSummaryQuery,
  useGetPendingCommissionsByEmployeeQuery,
  useMarkCommissionsAsPaidMutation,
  useCreateCommissionMutation,
} = commissionsApi;